import React, { useState, useEffect } from "react";
import RouterLinkButton from "../components/RouterLinkButton";
import { useAuth } from "@shadowhealth/oidc-login";
import { enqueueSnackbar } from "notistack";
import callApi from "../helpers/callApi";
import { Card, CardContent, Chip, Stack, Typography, TextField } from "@mui/material";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import NewScriptForm from "../components/NewScriptForm";
import useDebouncedValue from "../hooks/useDebouncedValue";

const ScriptsPage = () => {
  const auth = useAuth();
  const [scripts, setScripts] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const debouncedSearchText = useDebouncedValue(searchText.trim());
  const gridApiRef = useGridApiRef();
  const SCRIPTS_ENDPOINT = "/scripts";
  const PIPELINES_ENDPOINT = "/pipelines";

  const renderLink = (params) => {
    return (
      <RouterLinkButton
        to={"/scripts/" + params.value}
        variant="text"
        color="primary"
        primary={params.value}
        disableRipple
      />
    );
  };

  // Raw (authored) phase labels of the script's newest version; "—" when unphased.
  const renderPhases = (params) => {
    const phases = params.value || [];
    if (phases.length === 0) {
      return (
        <Typography variant="body2" color="text.disabled">
          —
        </Typography>
      );
    }
    return (
      <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5 }}>
        {phases.map((p) => (
          <Chip key={p} size="small" label={p} variant="outlined" color="secondary" />
        ))}
      </Stack>
    );
  };

  const columns = [
    { field: "id", headerName: "ID", width: 80 },
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 280,
      renderCell: renderLink,
      display: "flex",
    },
    {
      field: "phases",
      headerName: "Phases",
      flex: 1,
      minWidth: 240,
      sortable: false,
      renderCell: renderPhases,
      display: "flex",
    },
  ];

  useEffect(() => {
    getScripts();
  }, []);

  const getScripts = async () => {
    try {
      // GET /scripts returns [{ name, phases, phased }] so the list can show each
      // script's phases in one call (vs. fetching per row).
      const response = await callApi(SCRIPTS_ENDPOINT, "GET", undefined, auth.user?.access_token);
      const scripts = response.map((r, i) => {
        return {
          id: i,
          name: r.name,
          phases: r.phases || [],
          phased: r.phased,
        };
      });
      setScripts(scripts);
    } catch (err) {
      enqueueSnackbar(err.message, { variant: "error" });
    }
  };

  const newScript = async (scriptName, rubricName, rubricVersion) => {
    try {
      const body = {
        rubricName: rubricName || scriptName,
        rubricVersion,
        requestedBy: auth.user?.profile.email,
      };
      const response = await callApi(`${PIPELINES_ENDPOINT}/${scriptName}`, "POST", body, auth.user?.access_token);
      enqueueSnackbar(response, { variant: "info" });
      const newScript = {
        id: scripts.length,
        name: scriptName,
        phases: [],
        phased: false,
      };
      setScripts((old) => [...old, newScript]);
    } catch (err) {
      enqueueSnackbar(err.message, { variant: "error" });
    }
  };

  useEffect(() => {
    const trimmed = debouncedSearchText.trim();
    if (!trimmed) {
      setSearchLoading(false);
      setSearchResults([]);
      return;
    }

    let cancelled = false;
    setSearchLoading(true);
    (async () => {
      try {
        const response = await callApi(
          `${SCRIPTS_ENDPOINT}/search?query=${encodeURIComponent(trimmed)}`,
          "GET",
          undefined,
          auth.user?.access_token,
        );
        if (cancelled) return;
        setSearchResults(response);
      } catch (err) {
        if (cancelled) return;
        enqueueSnackbar(err.message, { variant: "error" });
        setSearchResults([]);
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [debouncedSearchText]);

  const trimmedSearchText = searchText.trim();
  const isSearching = trimmedSearchText.length > 0;
  const debouncing = isSearching && trimmedSearchText !== debouncedSearchText.trim();
  const searchRows = searchResults.map((r, i) => ({
    id: i,
    name: r.name,
    phases: r.phases || [],
    phased: r.phased,
  }));
  const rows = isSearching ? searchRows : scripts;
  const gridLoading = searchLoading || debouncing;
  const noRowsLabel = isSearching ? "No matches" : "No rows";

  const handleSearchKeyDown = (event) => {
    if (event.key !== "ArrowDown" || rows.length === 0) return;
    event.preventDefault();
    const firstRowId = gridApiRef.current.getRowIdFromRowIndex(0);
    gridApiRef.current.setCellFocus(firstRowId, "name");
  };

  return (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Stack direction="column" spacing={3}>
            <Stack direction="row" alignItems="baseline" spacing={1}>
              <Typography variant="h5" component="h1">
                Scripts
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Browse, create, and release script versions
              </Typography>
            </Stack>
            <TextField
              placeholder="Search script..."
              fullWidth
              inputProps={{ "aria-label": "Search script" }}
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
            <NewScriptForm onSubmit={newScript} />
          </Stack>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <DataGrid
            apiRef={gridApiRef}
            rows={rows}
            columns={columns}
            loading={gridLoading}
            localeText={{ noRowsLabel }}
            autoHeight
            density="comfortable"
            initialState={{
              sorting: {
                sortModel: [{ field: "name", sort: "asc" }],
              },
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            sx={{ border: 0 }}
          />
        </CardContent>
      </Card>
    </Stack>
  );
};

export default ScriptsPage;
